import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Crown, Check, X, Sparkles } from "lucide-react";

const SubscriptionModal = ({ onClose, onSuccess, currentSubscription }) => {
  const [loading, setLoading] = useState(false);
  const isPremium = currentSubscription?.subscription_type === "premium";

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/subscriptions/upgrade`);
      toast.success("Upgraded to Premium! Enjoy ad-free experience.");
      onSuccess();
    } catch (error) {
      console.error("Upgrade error:", error);
      toast.error("Failed to upgrade subscription");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/subscriptions/cancel`);
      toast.success("Subscription cancelled. You're now on free tier.");
      onSuccess();
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Failed to cancel subscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" data-testid="subscription-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative animate-in fade-in zoom-in duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="subscription-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            {isPremium ? "Premium Subscription" : "Upgrade to Premium"}
          </h2>
          <p className="text-gray-600">
            {isPremium ? "You're enjoying premium benefits" : "Enjoy ad-free sports tracking"}
          </p>
        </div>

        {/* Plans Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Free Plan */}
          <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Free</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">Live score updates</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">All sports coverage</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-5 h-5 text-red-500 mt-0.5" />
                <span className="text-gray-500 line-through">Ad-free experience</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-5 h-5 text-red-500 mt-0.5" />
                <span className="text-gray-500 line-through">Priority support</span>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-4">₹0<span className="text-sm font-normal text-gray-600">/month</span></div>
          </div>

          {/* Premium Plan */}
          <div className="border-2 border-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-6 bg-gradient-to-br from-yellow-50 to-orange-50 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Sparkles className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              Premium
              <Crown className="w-5 h-5 text-yellow-600" />
            </h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">Live score updates</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700">All sports coverage</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700 font-semibold">Ad-free experience</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-gray-700 font-semibold">Priority support</span>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-4">₹99<span className="text-sm font-normal text-gray-600">/month</span></div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          {isPremium ? (
            <>
              <Button
                onClick={handleCancel}
                variant="outline"
                className="flex-1"
                disabled={loading}
                data-testid="cancel-subscription-btn"
              >
                {loading ? "Processing..." : "Cancel Subscription"}
              </Button>
              <Button
                onClick={onClose}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                data-testid="close-subscription-btn"
              >
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
                data-testid="maybe-later-btn"
              >
                Maybe Later
              </Button>
              <Button
                onClick={handleUpgrade}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                disabled={loading}
                data-testid="upgrade-now-btn"
              >
                {loading ? "Processing..." : (
                  <>
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade Now
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;